import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  TableContainer, 
  Table, 
  TableBody, 
  TableRow, 
  TableCell, 
  Paper,
  Divider,
  Alert,
  AlertTitle
} from '@mui/material';
import { offices, subOffices, services, serviceQualityQuestions } from '../data/formData';
import FeedbackDialog from '../components/FeedbackDialog';

const FeedbackStep = ({ 
  suggestions, setSuggestions,
  email, setEmail,
  InternalExternal,
  clientType,
  gender,
  age,
  region,
  office,
  subOffice,
  service,
  otherSpecification,
  ccAwareness,
  ccVisibility,
  ccHelpfulness,
  satisfaction,
  activeStep,
  steps,
  isPaymentRelatedService,
  themeColors,
  errors
}) => {
  // Find the office label
  const getOfficeLabel = () => {
    const selectedOffice = offices.find(o => o.value === office);
    return selectedOffice ? selectedOffice.label : '';
  };
  
  // Find the sub-office label
  const getSubOfficeLabel = () => {
    if (!subOffice || !office || !subOffices[office]) return '';
    const selectedSubOffice = subOffices[office].find(so => so.value === subOffice);
    return selectedSubOffice ? selectedSubOffice.label : '';
  };
  
  // Find the service label
  const getServiceLabel = () => {
    const serviceList = subOffice ? services[subOffice] : services[office];
    if (!serviceList) return '';
    
    const selectedService = serviceList.find(s => s.value === service);
    return selectedService ? selectedService.label : '';
  };
  
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
  };
  
  // State for feedback dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Get negative ratings
  const getNegativeRatings = () => {
    const negativeRatings = ['Strongly Disagree', 'Disagree', 'Neutral'];
    const result = [];
    
    for (const [key, value] of Object.entries(satisfaction)) {
      // Skip SQD5 if service is not payment-related and it's set to N/A
      if (key === 'sqd5' && !isPaymentRelatedService() && value === 'N/A') {
        continue;
      }
      
      if (negativeRatings.includes(value)) {
        const question = serviceQualityQuestions.find(q => q.id === key);
        if (question) {
          result.push({
            question,
            rating: value
          });
        }
      }
    }
    
    return result;
  };
  
  // Check if there are any negative ratings that would require feedback
  const hasNegativeRatings = () => {
    return getNegativeRatings().length > 0;
  };
  
  // Show dialog if there are negative ratings and no feedback yet
  useEffect(() => {
    if (hasNegativeRatings() && !suggestions && activeStep === steps.length - 1) {
      setDialogOpen(true);
    }
  }, [activeStep, hasNegativeRatings, suggestions, steps.length]);
  
  // Handle dialog submission
  const handleDialogSubmit = () => {
    if (suggestions) {
      setDialogOpen(false);
    }
  };
  
  const isOthersSelected = service && service.includes('_others');

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ 
        fontSize: { xs: '1rem', sm: '1.25rem' },
        textAlign: { xs: 'center', sm: 'left' }
      }}>
        Suggestions on how we can further improve our services
      </Typography>
      
      {hasNegativeRatings() && (
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 2, 
            '& .MuiAlert-icon': { 
              color: themeColors.primary 
            }
          }}
        >
          <AlertTitle>Feedback Required</AlertTitle>
          You've selected "Strongly Disagree," "Disagree," or "Neutral" for one or more ratings. 
          Please provide specific feedback about why you chose these ratings so we can improve our services.
        </Alert>
      )}
      
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        {hasNegativeRatings() 
          ? "Required - Please let us know why you gave lower ratings and how we can improve" 
          : "(Optional) Your feedback helps us serve you better."}
      </Typography>
      
      <TextField
        fullWidth
        multiline
        rows={3}
        value={suggestions}
        onChange={(e) => setSuggestions(e.target.value)}
        error={hasNegativeRatings() && !suggestions && errors.suggestions}
        required={hasNegativeRatings()}
        helperText={hasNegativeRatings() && !suggestions && errors.suggestions ? "Feedback is required for negative ratings" : ""}
        sx={{ 
          mb: 3,
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
              borderColor: themeColors.primary,
            },
          },
        }}
      />

      <Typography variant="h6" gutterBottom sx={{ 
        fontSize: { xs: '1rem', sm: '1.25rem' },
        textAlign: { xs: 'center', sm: 'left' }
      }}>
        Contact Information
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Email Address
      </Typography>
      <TextField
        fullWidth
        value={email}
        onChange={handleEmailChange}
        required
        placeholder="Email"
        error={errors && errors.email}
        helperText={errors && errors.email ? "Please enter a valid email address" : "Email is required"}
        sx={{ 
          mb: 3,
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
              borderColor: themeColors.primary,
            },
          },
        }}
      />

      {activeStep === steps.length - 1 && (
        <Box mt={3}>
          <Typography variant="h6" gutterBottom sx={{ 
            fontSize: { xs: '1rem', sm: '1.25rem' },
            textAlign: { xs: 'center', sm: 'left' },
            borderBottom: `2px solid ${themeColors.primary}`,
            pb: 1,
            mb: 2
          }}>
            Summary of your answers
          </Typography>
          
          <Paper elevation={2} sx={{ 
            border: `1px solid ${themeColors.borderColor}`,
            overflow: 'hidden',
            mb: 3
          }}>
            <Box sx={{ 
              py: 1, 
              px: 2, 
              backgroundColor: themeColors.primary, 
              color: 'white'
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Personal Information
              </Typography>
            </Box>
            
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ 
                      backgroundColor: themeColors.bgLight, 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      width: '40%'
                    }}>Internal or External</TableCell>
                    <TableCell align="right" sx={{ 
                      color: themeColors.primary, 
                      fontWeight: 500, 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' } 
                    }}>{InternalExternal}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ 
                      backgroundColor: themeColors.bgLight, 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      width: '40%'
                    }}>Client Type</TableCell>
                    <TableCell align="right" sx={{ 
                      color: themeColors.primary, 
                      fontWeight: 500, 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' } 
                    }}>{clientType}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ 
                      backgroundColor: themeColors.bgLight, 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' } 
                    }}>Sex</TableCell>
                    <TableCell align="right" sx={{ 
                      color: themeColors.primary, 
                      fontWeight: 500, 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' } 
                    }}>{gender}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ 
                      backgroundColor: themeColors.bgLight, 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' } 
                    }}>Age</TableCell>
                    <TableCell align="right" sx={{ 
                      color: themeColors.primary, 
                      fontWeight: 500, 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' } 
                    }}>{age}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ 
                      backgroundColor: themeColors.bgLight, 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' } 
                    }}>Region of Residence</TableCell>
                    <TableCell align="right" sx={{ 
                      color: themeColors.primary, 
                      fontWeight: 500, 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      wordBreak: 'break-word' 
                    }}>{region}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          
          <Paper elevation={2} sx={{ 
            border: `1px solid ${themeColors.borderColor}`,
            overflow: 'hidden',
            mb: 3
          }}>
            <Box sx={{ 
              py: 1, 
              px: 2, 
              backgroundColor: themeColors.primary, 
              color: 'white'
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Service Information
              </Typography>
            </Box>
            
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ 
                      backgroundColor: themeColors.bgLight, 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      width: '40%'
                    }}>Office</TableCell>
                    <TableCell align="right" sx={{ 
                      color: themeColors.primary, 
                      fontWeight: 500, 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      wordBreak: 'break-word'
                    }}>
                      {getOfficeLabel()}
                    </TableCell>
                  </TableRow>
                  
                  {subOffice && (
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ 
                        backgroundColor: themeColors.bgLight, 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' } 
                      }}>Sub-Office</TableCell>
                      <TableCell align="right" sx={{ 
                        color: themeColors.primary, 
                        fontWeight: 500, 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        wordBreak: 'break-word'
                      }}>
                        {getSubOfficeLabel()}
                      </TableCell>
                    </TableRow>
                  )}
                  
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ 
                      backgroundColor: themeColors.bgLight, 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' } 
                    }}>Service Availed</TableCell>
                    <TableCell align="right" sx={{ 
                      color: themeColors.primary, 
                      fontWeight: 500, 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      wordBreak: 'break-word'
                    }}>
                      {getServiceLabel()}
                      {isOthersSelected && otherSpecification && `: ${otherSpecification}`}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          
          <Paper elevation={2} sx={{ 
            border: `1px solid ${themeColors.borderColor}`,
            overflow: 'hidden',
            mb: 3
          }}>
            <Box sx={{ 
              py: 1, 
              px: 2, 
              backgroundColor: themeColors.primary, 
              color: 'white'
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Citizen's Charter Feedback
              </Typography>
            </Box>
            
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ 
                      backgroundColor: themeColors.bgLight, 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      width: '40%'
                    }}>CC Awareness</TableCell>
                    <TableCell align="right" sx={{ 
                      color: themeColors.primary, 
                      fontWeight: 500, 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      wordBreak: 'break-word'
                    }}>{ccAwareness}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ 
                      backgroundColor: themeColors.bgLight, 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' } 
                    }}>CC Visibility</TableCell>
                    <TableCell align="right" sx={{ 
                      color: themeColors.primary, 
                      fontWeight: 500, 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}>{ccVisibility}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row" sx={{ 
                      backgroundColor: themeColors.bgLight, 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' } 
                    }}>CC Helpfulness</TableCell>
                    <TableCell align="right" sx={{ 
                      color: themeColors.primary, 
                      fontWeight: 500, 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}>{ccHelpfulness}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          
          <Paper elevation={2} sx={{ 
            border: `1px solid ${themeColors.borderColor}`,
            overflow: 'hidden'
          }}>
            <Box sx={{ 
              py: 1, 
              px: 2, 
              backgroundColor: themeColors.primary, 
              color: 'white'
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Service Quality Ratings
              </Typography>
            </Box>
            
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {Object.entries(satisfaction).map(([key, value]) => {
                    // Skip displaying SQD5 if service is not payment-related
                    if (key === 'sqd5' && !isPaymentRelatedService()) {
                      return null;
                    }
                    
                    // Find the question label based on the key
                    const question = serviceQualityQuestions.find(q => q.id === key);
                    const questionLabel = question ? question.label : '';
                    
                    if (value && questionLabel) {
                      return (
                        <TableRow key={key}>
                          <TableCell component="th" scope="row" sx={{ 
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            padding: { xs: '6px', sm: '8px' },
                            wordBreak: 'break-word',
                            backgroundColor: themeColors.bgLight,
                            width: '70%'
                          }}>{questionLabel}</TableCell>
                          <TableCell align="right" sx={{ 
                            padding: { xs: '6px', sm: '8px' },
                            fontSize: { xs: '0.7rem', sm: '0.75rem' }
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                              {value === 'Strongly Agree' && <span style={{ color: 'green', marginRight: '5px' }}>😊</span>}
                              {value === 'Agree' && <span style={{ color: 'green', marginRight: '5px' }}>🙂</span>}
                              {value === 'Neutral' && <span style={{ color: 'gray', marginRight: '5px' }}>😐</span>}
                              {value === 'Disagree' && <span style={{ color: 'red', marginRight: '5px' }}>🙁</span>}
                              {value === 'Strongly Disagree' && <span style={{ color: 'red', marginRight: '5px' }}>😠</span>}
                              {value === 'N/A' && <span style={{ color: 'gray', marginRight: '5px' }}>❓</span>}
                              <span style={{ color: value === 'Strongly Agree' || value === 'Agree' ? 'green' : 
                                            value === 'Disagree' || value === 'Strongly Disagree' ? 'red' : 'gray' }}>
                                {value}
                              </span>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    return null;
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          
          {/* Optional section for suggestions and contact info */}
          {(suggestions || email) && (
            <Paper elevation={2} sx={{ 
              border: `1px solid ${themeColors.borderColor}`,
              overflow: 'hidden',
              mt: 3,
              p: 2
            }}>
              {suggestions && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: themeColors.primary }}>
                    Your Feedback:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, mt: 1, whiteSpace: 'pre-line' }}>
                    {suggestions}
                  </Typography>
                </>
              )}
              
              {email && (
                <>
                  {suggestions && <Divider sx={{ my: 2 }} />}
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: themeColors.primary }}>
                    Contact Information:
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {email}
                  </Typography>
                </>
              )}
            </Paper>
          )}
        </Box>
      )}
      
      {/* Feedback Dialog for Negative Ratings */}
      <FeedbackDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleDialogSubmit}
        feedback={suggestions}
        setFeedback={setSuggestions}
        themeColors={themeColors}
        negativeRatings={getNegativeRatings()}
      />
    </Box>
  );
};

export default FeedbackStep;